import { TranslocoModule, provideTranslocoScope, translate } from '@jsverse/transloco';
import { Component, computed, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslocoCurrencyPipe, TranslocoDecimalPipe } from '@jsverse/transloco-locale';
import { CanDirective, CapabilityNames, HeaderService } from 'core';
import { LoadingSpinner } from 'design-system';
import {
  CartRow,
  CartService,
  EditPriceDialog,
  EditPriceDialogResult,
  RemoveFromCartDialog,
  RemoveFromCartDialogData,
} from 'products';
import { downloadBlob } from 'receipts';
import { EMPTY, finalize, map, switchMap } from 'rxjs';
import { OrderProposalService } from '../../services/order-proposal.service';
import { ProposalAnomalies } from '../proposal-anomalies/proposal-anomalies';
import { ProposalSentDialog } from './proposal-sent-dialog/proposal-sent-dialog';
import { QuoteAuthorizationDialog } from './quote-authorization-dialog/quote-authorization-dialog';
import { SendProposalDialog, SendProposalDialogData } from './send-proposal-dialog/send-proposal-dialog';

@Component({
  providers: [provideTranslocoScope('orders')],
  selector: 'ord-order-summary',
  imports: [
    MatButtonModule,
    MatDividerModule,
    TranslocoModule,
    TranslocoCurrencyPipe,
    TranslocoDecimalPipe,
    CanDirective,
    LoadingSpinner,
    ProposalAnomalies,
  ],
  templateUrl: './order-summary.html',
  styleUrl: './order-summary.scss',
})
export class OrderSummary implements OnInit, OnDestroy {
  private readonly headerService = inject(HeaderService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly dialog = inject(MatDialog);
  private readonly snackBar = inject(MatSnackBar);
  private readonly cartService = inject(CartService);
  private readonly proposalService = inject(OrderProposalService);

  readonly capabilityNames = CapabilityNames;
  readonly proposal = this.proposalService.proposal;
  readonly cart = this.cartService.cart;

  // Righe del carrello della proposta, escluse quelle archiviate
  readonly rows = computed(() => (this.cart()?.righe ?? []).filter(row => !row.archiviata));

  readonly customerCodes = computed(() => {
    const proposal = this.proposal();
    return proposal ? [proposal.oil.codiceCliente, proposal.nonOil.codiceCliente].filter(code => code != null).join(' - ') : '';
  });

  // Preventivo in attesa di decisione: la proposta non si invia e non si rigenera
  readonly quoteInAuthorization = computed(() => this.proposal()?.preventivoStato === 'InAutorizzazione');
  readonly busy = signal(false);

  ngOnInit(): void {
    this.headerService.setTitle('orders.ui.riepilogoPropostaDOrdine');

    this.proposalService.ensure(this.route.snapshot.queryParamMap.get('id') ?? '')
      .pipe(switchMap(proposal => this.cartService.getCart(proposal.carrello.id)))
      .subscribe();
  }

  ngOnDestroy(): void {
    this.headerService.removeTitle();
  }

  increment(row: CartRow): void {
    this.updateQuantity(row, row.quantita + 1);
  }

  decrement(row: CartRow): void {
    if (row.quantita > 1) {
      this.updateQuantity(row, row.quantita - 1);
    }
  }

  // Quantità 0 = il backend elimina la riga dal carrello
  remove(row: CartRow): void {
    const data: RemoveFromCartDialogData = {
      nomeProdotto: row.nomeProdotto,
      nomeImballo: row.nomeImballo,
      unitaFatturabile: row.unitaFatturabile,
      unitaMisura: row.unitaMisura,
    };

    this.dialog.open(RemoveFromCartDialog, {
      width: '400px',
      data,
    }).afterClosed().subscribe(confirmed => {
      if (confirmed) {
        this.updateQuantity(row, 0);
      }
    });
  }

  editPrice(row: CartRow): void {
    this.dialog.open(EditPriceDialog, {
      width: '400px',
      data: {
        nomeProdotto: row.nomeProdotto,
        prezzo: row.prezzo,
        unitaMisura: row.unitaMisura,
        prezzoMinimo: row.prezzoMinimo,
      },
    }).afterClosed().subscribe((result?: EditPriceDialogResult) => {
      if (result) {
        this.cartService.updateProduct(row.idProdotto, row.quantita, result.price).subscribe({
          error: error => {
            console.error('Errore durante l\'aggiornamento del prezzo', error);
          },
        });
      }
    });
  }

  // Generato si scarica; in attesa di autorizzazione arriverà una notifica
  generateQuote(): void {
    this.busy.set(true);
    this.proposalService.generateQuote().pipe(
      switchMap(proposal => proposal.preventivoStato === 'Generato'
        ? this.proposalService.downloadQuote()
        : this.dialog.open(QuoteAuthorizationDialog, { width: '400px' }).afterClosed().pipe(map(() => null))),
      finalize(() => this.busy.set(false)),
    ).subscribe({
      next: quote => {
        if (quote) {
          downloadBlob(quote.blob, quote.fileName);
        }
      },
      error: error => this.showError(error),
    });
  }

  sendProposal(): void {
    const proposal = this.proposal()!;
    this.dialog.open<SendProposalDialog, SendProposalDialogData, { notify: boolean }>(SendProposalDialog, {
      width: '400px',
      disableClose: true,
      data: { numero: proposal.numero, dataInserimento: proposal.dataInserimento },
    }).afterClosed().pipe(
      switchMap(result => {
        if (!result) {
          return EMPTY;
        }
        this.busy.set(true);
        return this.proposalService.send(result.notify).pipe(finalize(() => this.busy.set(false)));
      }),
    ).subscribe({
      next: sent => {
        this.dialog.open(ProposalSentDialog, {
          width: '400px',
          disableClose: true,
          data: { inAuthorization: sent.stato === 'InAutorizzazione' },
        }).afterClosed().subscribe(() => {
          void this.router.navigate(['/orders/list'], { queryParams: { view: 'drafts' }, replaceUrl: true });
        });
      },
      error: error => this.showError(error),
    });
  }

  private updateQuantity(row: CartRow, quantity: number): void {
    this.cartService.updateProduct(row.idProdotto, quantity, row.prezzo).subscribe({
      error: error => {
        console.error('Errore durante l\'aggiornamento della quantità', error);
      },
    });
  }

  // Il 400 lo mostra già il messaggio globale col motivo del backend
  private showError(error: HttpErrorResponse): void {
    if (error.status !== 400) {
      this.snackBar.open(translate<string>('orders.ui.operazioneNonEseguita'), translate<string>('orders.ui.chiudi'), { duration: 5000 });
    }
  }
}
